import { getDbSupabase } from './supabase';
import {
  extractSingaporePostal,
  resolveDistrictFromAddress,
  resolveDistrictFromPostal,
} from '../utils/singaporeDistrict';

export type PurchaseMonthTrend = 'up' | 'down' | 'flat' | 'new';
export type PurchaseItemStatus = 'stopped' | 'declining' | 'ok';

export type PurchaseSortKey =
  | 'item_no'
  | 'description'
  | 'base_uom'
  | 'unit_price'
  | 'qty_ytd'
  | 'qty_mtd'
  | 'qty_prev_month'
  | 'qty_month_est'
  | 'month_trend'
  | 'status'
  | 'outlet_code'
  | 'outlet_name'
  | 'ipg'
  | 'inventory'
  | 'fsm'
  | 'ta'
  | 'packing'
  | 'qty_m01'
  | 'qty_m02'
  | 'qty_m03'
  | 'qty_m04'
  | 'qty_m05'
  | 'qty_m06'
  | 'qty_m07'
  | 'qty_m08'
  | 'qty_m09'
  | 'qty_m10'
  | 'qty_m11'
  | 'qty_m12';

export interface AccountPurchaseOutlet {
  code: string;
  name: string;
  /** NAV / DEM 6-digit postal when known. */
  postal?: string;
  /** Ship-to address when known (used as postal fallback). */
  address?: string;
}

export interface AccountPurchaseItem {
  outlet_code: string;
  outlet_name: string;
  item_no: string;
  description: string;
  base_uom: string;
  ipg: string;
  inventory: number;
  fsm: number;
  packing: string;
  ta: number | null;
  /** Jan–Dec qty for meta.year (index 0 = January). */
  qty_months: number[];
  qty_ytd: number;
  unit_price: number | null;
  qty_mtd: number;
  qty_prev_month: number;
  qty_month_est: number;
  month_trend: PurchaseMonthTrend;
  status: PurchaseItemStatus;
}

export interface AccountPurchaseMeta {
  mother_code: string;
  today?: string;
  year?: number;
  year_start?: string;
  current_month?: number;
  day_of_month?: number;
  days_in_month?: number;
  month_start?: string;
  prev_month_start?: string;
}

export interface PurchasePeriodLabels {
  year: number;
  /** Short month headers Jan…Dec */
  monthLabels: string[];
  /** How many months to highlight as “to date” (1–12). */
  currentMonth: number;
  legend: string;
  est: string;
  currentMonthLabel: string;
  prevMonthLabel: string;
}

/** Promo / non-stock IPGs hidden across Purchase tabs. */
const HIDDEN_PURCHASE_IPGS = new Set(['FG-AP', 'FG-OB', 'FG-OBX']);

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MONTH_SORT_KEYS: PurchaseSortKey[] = [
  'qty_m01',
  'qty_m02',
  'qty_m03',
  'qty_m04',
  'qty_m05',
  'qty_m06',
  'qty_m07',
  'qty_m08',
  'qty_m09',
  'qty_m10',
  'qty_m11',
  'qty_m12',
];

export function isHiddenPurchaseIpg(ipg: string): boolean {
  return HIDDEN_PURCHASE_IPGS.has(ipg.trim().toUpperCase());
}

/** @deprecated Use isHiddenPurchaseIpg */
export function isHiddenUnitPriceIpg(ipg: string): boolean {
  return isHiddenPurchaseIpg(ipg);
}

export function excludeHiddenPurchaseIpgs(
  items: AccountPurchaseItem[],
): AccountPurchaseItem[] {
  return items.filter((item) => !isHiddenPurchaseIpg(item.ipg));
}

/** Item codes ending in -B / -B1 / -B2… (backup / alternate SKUs). */
export function isBackupItemCode(itemNo: string): boolean {
  return /-B\d*$/i.test(itemNo.trim());
}

export function splitPrimaryBackupItems(items: AccountPurchaseItem[]): {
  primary: AccountPurchaseItem[];
  backup: AccountPurchaseItem[];
} {
  const primary: AccountPurchaseItem[] = [];
  const backup: AccountPurchaseItem[] = [];
  for (const item of items) {
    if (isBackupItemCode(item.item_no)) backup.push(item);
    else primary.push(item);
  }
  return { primary, backup };
}

function parseMetaDate(value?: string): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function emptyMonths(): number[] {
  return Array.from({ length: 12 }, () => 0);
}

function normalizeMonths(raw: unknown): number[] {
  const out = emptyMonths();
  if (!Array.isArray(raw)) return out;
  for (let i = 0; i < 12; i += 1) {
    out[i] = num(raw[i]);
  }
  return out;
}

export function monthQty(item: AccountPurchaseItem, monthIndex: number): number {
  if (monthIndex < 0 || monthIndex > 11) return 0;
  return item.qty_months[monthIndex] ?? 0;
}

export function monthSortKey(monthIndex: number): PurchaseSortKey {
  return MONTH_SORT_KEYS[monthIndex] ?? 'qty_m01';
}

export function buildPurchasePeriodLabels(meta: AccountPurchaseMeta): PurchasePeriodLabels {
  const today = parseMetaDate(meta.today) ?? new Date();
  const year = meta.year ?? today.getFullYear();
  const currentMonth = Math.min(12, Math.max(1, meta.current_month ?? today.getMonth() + 1));
  const monthStart =
    parseMetaDate(meta.month_start) ?? new Date(year, currentMonth - 1, 1);
  const prevMonthStart =
    parseMetaDate(meta.prev_month_start) ?? new Date(year, currentMonth - 2, 1);

  const mm = String(monthStart.getMonth() + 1).padStart(2, '0');
  const prevMm = String(prevMonthStart.getMonth() + 1).padStart(2, '0');
  const prevYear = prevMonthStart.getFullYear();

  return {
    year,
    monthLabels: MONTH_SHORT.map((label) => `${label}`),
    currentMonth,
    est: `${mm} est.`,
    currentMonthLabel: `${mm}/${year}`,
    prevMonthLabel: `${prevMm}/${prevYear}`,
    legend: `${year} monthly usage (Jan–Dec). Current month is month-to-date; future months are blank until posted.`,
  };
}

export interface AccountPurchaseSummary {
  outlets: AccountPurchaseOutlet[];
  items: AccountPurchaseItem[];
  meta: AccountPurchaseMeta;
}

/** Sort S0646, S0646-S1, S0646-S2, … S0646-S10 (not lexical). */
export function sortOutletsNaturally(
  outlets: AccountPurchaseOutlet[],
  motherCode: string,
): AccountPurchaseOutlet[] {
  const mother = motherCode.trim().toUpperCase();
  const sortKey = (code: string): [number, number, string] => {
    const c = code.trim().toUpperCase();
    if (c === mother) return [0, 0, c];
    const m = /^(.+)-S(\d+)$/i.exec(c);
    if (m && m[1] === mother) return [1, Number.parseInt(m[2], 10), c];
    if (c.startsWith(`${mother}-`)) return [1, 999999, c];
    return [2, 0, c];
  };
  return [...outlets].sort((a, b) => {
    const ka = sortKey(a.code);
    const kb = sortKey(b.code);
    if (ka[0] !== kb[0]) return ka[0] - kb[0];
    if (ka[1] !== kb[1]) return ka[1] - kb[1];
    return ka[2].localeCompare(kb[2]);
  });
}

export function defaultOutletCode(
  outlets: AccountPurchaseOutlet[],
  motherCode: string,
): string {
  const sorted = sortOutletsNaturally(outlets, motherCode);
  const sub = sorted.find((o) => /-S\d+$/i.test(o.code));
  return sub?.code ?? sorted[0]?.code ?? '';
}

const OUTLET_PLACE_PHRASES = [
  'ion orchard',
  '313 somerset',
  'funan',
  'vivocity',
  'plaza singapura',
  'raffles city',
  'suntec city',
  'marina bay sands',
  'jurong point',
  'causeway point',
  'waterway point',
  'east point',
  'city square',
  'thomson plaza',
  'northpoint',
  'westgate',
  'nex',
  'jem',
  'imm',
  'lot one',
  'changi airport',
].sort((a, b) => b.length - a.length);

function matchKnownOutletPlace(text: string): string {
  const lower = text.toLowerCase();
  for (const phrase of OUTLET_PLACE_PHRASES) {
    if (!lower.includes(phrase)) continue;
    return phrase
      .split(' ')
      .map((w) =>
        ['ion', 'imm', 'jem', 'nex'].includes(w) ? w.toUpperCase() : w.replace(/^[a-z]/, (c) => c.toUpperCase()),
      )
      .join(' ');
  }
  return '';
}

/**
 * Short place label for an outlet.
 * Prefer postal → district (reliable); fall back to address / light name hints.
 */
export function outletLocationLabel(
  name: string,
  code = '',
  postal = '',
  address = '',
): string {
  const fromPostal = resolveDistrictFromPostal(postal);
  if (fromPostal) return fromPostal;

  const addr = address.trim() || name.trim();
  const postalInText = extractSingaporePostal(addr) || extractSingaporePostal(name);
  if (postalInText) {
    const fromTextPostal = resolveDistrictFromPostal(postalInText);
    if (fromTextPostal) return fromTextPostal;
  }

  if (addr) {
    const fromAddress = resolveDistrictFromAddress(addr);
    if (fromAddress) return fromAddress;
  }

  // Last resort: known mall phrase in NAV name (not "@ company").
  const raw = (name.trim() || code.trim()).replace(/\s+/g, ' ');
  if (!raw) return '';
  const known = matchKnownOutletPlace(raw);
  if (known) return known;

  return '';
}

/** Short suffix like S8 from S0366-S8; mother code returns empty. */
export function outletShortCode(code: string, motherCode = ''): string {
  const c = code.trim().toUpperCase();
  const mother = motherCode.trim().toUpperCase();
  if (!c) return '';
  if (mother && c === mother) return '';
  const m = /-S(\d+)$/i.exec(c);
  if (m) return `S${m[1]}`;
  if (mother && c.startsWith(`${mother}-`)) return c.slice(mother.length + 1);
  return c;
}

/** Dropdown / list label: district only (no postal). */
export function formatOutletChoiceLabel(
  outlet: Pick<AccountPurchaseOutlet, 'code' | 'name'> & {
    postal?: string;
    address?: string;
  },
  motherCode = '',
): string {
  const code = outlet.code.trim();
  const mother = motherCode.trim();
  const location = outletLocationLabel(
    outlet.name,
    code,
    outlet.postal ?? '',
    outlet.address ?? '',
  );
  const short = outletShortCode(code, mother);

  if (mother && code.toUpperCase() === mother.toUpperCase()) {
    return location || 'Mother account';
  }
  if (location) return location;
  if (short) return short;
  return code;
}

export function normalizeOutletPostal(
  postal?: string | null,
  address?: string | null,
  name?: string | null,
): string {
  const direct = (postal ?? '').replace(/\D/g, '');
  if (direct.length >= 6) return direct.slice(-6);
  return (
    extractSingaporePostal(address) ||
    extractSingaporePostal(name) ||
    ''
  );
}

function outletDedupeKey(
  outlet: AccountPurchaseOutlet,
  motherCode = '',
): string {
  const location = outletLocationLabel(
    outlet.name,
    outlet.code,
    outlet.postal ?? '',
    outlet.address ?? '',
  )
    .trim()
    .toLowerCase();
  if (location) return `district:${location}`;
  const postal = normalizeOutletPostal(outlet.postal, outlet.address, outlet.name);
  if (postal) return `postal:${postal}`;
  return `code:${outlet.code.trim().toUpperCase() || motherCode}`;
}

/**
 * One row per district/area (same district + different postals → keep first).
 * Falls back to postal, then code, when district is unknown.
 */
export function dedupeOutletsByDistrict(
  outlets: AccountPurchaseOutlet[],
  motherCode = '',
): AccountPurchaseOutlet[] {
  const sorted = sortOutletsNaturally(outlets, motherCode);
  const seen = new Set<string>();
  const result: AccountPurchaseOutlet[] = [];

  for (const outlet of sorted) {
    const key = outletDedupeKey(outlet, motherCode);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(outlet);
  }
  return result;
}

/** @deprecated Use dedupeOutletsByDistrict */
export function dedupeOutletsByPostal(
  outlets: AccountPurchaseOutlet[],
  motherCode = '',
): AccountPurchaseOutlet[] {
  return dedupeOutletsByDistrict(outlets, motherCode);
}

/** Map a saved outlet onto the deduped option that shares district/postal. */
export function resolveOutletOptionCode(
  savedCode: string,
  options: AccountPurchaseOutlet[],
  allOutlets: AccountPurchaseOutlet[],
  motherCode = '',
): string {
  const code = savedCode.trim();
  if (!code) return '';
  if (options.some((o) => o.code.toUpperCase() === code.toUpperCase())) return code;

  const saved = allOutlets.find((o) => o.code.toUpperCase() === code.toUpperCase());
  if (!saved) return code;

  const key = outletDedupeKey(saved, motherCode);
  const match = options.find((o) => outletDedupeKey(o, motherCode) === key);
  return match?.code ?? code;
}

async function enrichOutletsWithDemPostal(
  outlets: AccountPurchaseOutlet[],
): Promise<AccountPurchaseOutlet[]> {
  const codes = [
    ...new Set(outlets.map((o) => o.code.trim()).filter(Boolean)),
  ];
  if (codes.length === 0) return outlets;

  try {
    const db = getDbSupabase('dem');
    const { data, error } = await db
      .from('customers')
      .select('code,address,address_2,postal')
      .in('code', codes);
    if (error || !Array.isArray(data)) return outlets;

    const byCode = new Map<string, { address: string; postal: string }>();
    for (const row of data) {
      const code = String((row as { code?: string }).code ?? '').trim();
      if (!code) continue;
      const address = String((row as { address?: string }).address ?? '').trim();
      const address2 = String((row as { address_2?: string }).address_2 ?? '').trim();
      const postalField = String((row as { postal?: string }).postal ?? '').trim();
      const joined = [address, address2].filter(Boolean).join(' ');
      const postal =
        normalizeOutletPostal(postalField, joined, '') ||
        extractSingaporePostal(joined) ||
        '';
      byCode.set(code.toUpperCase(), {
        address: joined || address,
        postal,
      });
    }

    return outlets.map((outlet) => {
      const hit = byCode.get(outlet.code.trim().toUpperCase());
      if (!hit) return outlet;
      return {
        ...outlet,
        postal: outlet.postal || hit.postal || undefined,
        address: outlet.address || hit.address || undefined,
      };
    });
  } catch {
    return outlets;
  }
}

async function fetchOutletsFromDem(motherCode: string): Promise<AccountPurchaseOutlet[]> {
  const mother = motherCode.trim().toUpperCase();
  if (!mother) return [];
  try {
    const db = getDbSupabase('dem');
    const { data, error } = await db
      .from('customers')
      .select('code,name,address,address_2,postal,bill_to_customer_no')
      .or(
        `code.eq.${mother},code.ilike.${mother}-*,bill_to_customer_no.eq.${mother}`,
      )
      .limit(500);
    if (error || !Array.isArray(data) || data.length === 0) return [];

    return data
      .map((row) => {
        const code = String((row as { code?: string }).code ?? '').trim();
        const name = String((row as { name?: string }).name ?? '').trim() || code;
        const address = String((row as { address?: string }).address ?? '').trim();
        const address2 = String((row as { address_2?: string }).address_2 ?? '').trim();
        const postalField = String((row as { postal?: string }).postal ?? '').trim();
        const joined = [address, address2].filter(Boolean).join(' ');
        const postal =
          normalizeOutletPostal(postalField, joined, name) ||
          extractSingaporePostal(joined) ||
          '';
        return {
          code,
          name,
          postal: postal || undefined,
          address: joined || address || undefined,
        };
      })
      .filter((o) => o.code);
  } catch {
    return [];
  }
}

async function fetchOutletsFromLightApi(motherCode: string): Promise<AccountPurchaseOutlet[]> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  try {
    const { data } = await getDbSupabase('crm').auth.getSession();
    const token = data.session?.access_token?.trim();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // proxy rejects unauthenticated
  }

  const response = await fetch(
    `/account-sales-api?mother_code=${encodeURIComponent(motherCode)}&resource=outlets`,
    { headers },
  );
  const json = (await response.json().catch(() => null)) as
    | { outlets?: unknown; error?: string }
    | null;
  if (!response.ok) {
    throw new Error(json?.error || `Failed to load outlets (${response.status}).`);
  }

  const raw = Array.isArray(json?.outlets) ? json.outlets : [];
  return raw
    .filter((o): o is Record<string, unknown> => !!o && typeof o === 'object')
    .map((o) => {
      const code = str(o.code) || str(o.no);
      const address =
        str(o.address) || [str(o.address_2), str(o.city)].filter(Boolean).join(', ');
      const postal =
        str(o.postal) ||
        str(o.post_code) ||
        extractSingaporePostal(address) ||
        '';
      return {
        code,
        name: str(o.name) || code,
        postal: postal || undefined,
        address: address || undefined,
      };
    })
    .filter((o) => o.code);
}

const outletCache = new Map<string, { at: number; rows: AccountPurchaseOutlet[] }>();
const OUTLET_CACHE_MS = 5 * 60 * 1000;

/**
 * Fast outlets for task picker: DEM / light customer_list first (not full purchase RPC).
 * Results are cached briefly per mother code.
 */
export async function fetchAccountOutlets(motherCode: string): Promise<AccountPurchaseOutlet[]> {
  const code = motherCode.trim().toUpperCase();
  if (!code) return [];

  const cached = outletCache.get(code);
  if (cached && Date.now() - cached.at < OUTLET_CACHE_MS) {
    return cached.rows;
  }

  // Race DEM + light API; prefer the richer list (mother + children), not merely first non-empty.
  const demPromise = fetchOutletsFromDem(code);
  const apiPromise = fetchOutletsFromLightApi(code).catch(() => [] as AccountPurchaseOutlet[]);

  const [demRows, apiRows] = await Promise.all([
    demPromise.catch(() => [] as AccountPurchaseOutlet[]),
    apiPromise,
  ]);
  const rows = demRows.length >= apiRows.length ? demRows : apiRows;

  let finalRows = rows;

  // Last resort: purchase summary outlets (slow).
  if (finalRows.length === 0) {
    try {
      const summary = await fetchAccountPurchaseSummary(code);
      finalRows = await enrichOutletsWithDemPostal(summary.outlets);
    } catch {
      finalRows = [];
    }
  } else if (!finalRows.some((r) => r.postal)) {
    finalRows = await enrichOutletsWithDemPostal(finalRows);
  }

  const sorted = sortOutletsNaturally(finalRows, code);
  outletCache.set(code, { at: Date.now(), rows: sorted });
  return sorted;
}

function num(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function recomputeTrendStatus(
  row: AccountPurchaseItem,
  currentMonth = new Date().getMonth() + 1,
): AccountPurchaseItem {
  let month_trend: PurchaseMonthTrend = 'flat';
  if (row.qty_prev_month === 0 && row.qty_month_est > 0) month_trend = 'new';
  else if (row.qty_prev_month === 0 && row.qty_month_est === 0) month_trend = 'flat';
  else if (
    Math.abs(row.qty_month_est - row.qty_prev_month) / Math.abs(row.qty_prev_month) < 0.05
  ) {
    month_trend = 'flat';
  } else if (row.qty_month_est > row.qty_prev_month) month_trend = 'up';
  else if (row.qty_month_est < row.qty_prev_month) month_trend = 'down';

  let prior = 0;
  for (let i = 0; i < Math.max(0, currentMonth - 2); i += 1) {
    prior += row.qty_months[i] ?? 0;
  }

  let status: PurchaseItemStatus = 'ok';
  if (prior > 0 && row.qty_prev_month === 0 && row.qty_mtd === 0) status = 'stopped';
  else if (row.qty_prev_month > 0 && row.qty_month_est < row.qty_prev_month * 0.7) {
    status = 'declining';
  }

  return { ...row, month_trend, status };
}

function normalizeItem(raw: Record<string, unknown>): AccountPurchaseItem {
  const trend = str(raw.month_trend, 'flat');
  const status = str(raw.status, 'ok');
  const qty_months = normalizeMonths(raw.qty_months);
  const qty_ytd =
    raw.qty_ytd == null || raw.qty_ytd === ''
      ? qty_months.reduce((sum, q) => sum + q, 0)
      : num(raw.qty_ytd);
  return {
    outlet_code: str(raw.outlet_code),
    outlet_name: str(raw.outlet_name) || str(raw.outlet_code),
    item_no: str(raw.item_no),
    description: str(raw.description) || str(raw.item_no),
    base_uom: str(raw.base_uom),
    ipg: str(raw.ipg),
    inventory: num(raw.inventory),
    fsm: num(raw.fsm),
    packing: str(raw.packing),
    ta: raw.ta == null || raw.ta === '' ? null : num(raw.ta),
    qty_months,
    qty_ytd,
    unit_price: raw.unit_price == null || raw.unit_price === '' ? null : num(raw.unit_price),
    qty_mtd: num(raw.qty_mtd),
    qty_prev_month: num(raw.qty_prev_month),
    qty_month_est: num(raw.qty_month_est),
    month_trend: (['up', 'down', 'flat', 'new'].includes(trend)
      ? trend
      : 'flat') as PurchaseMonthTrend,
    status: (['stopped', 'declining', 'ok'].includes(status)
      ? status
      : 'ok') as PurchaseItemStatus,
  };
}

export function aggregatePurchaseItems(
  items: AccountPurchaseItem[],
  currentMonth?: number,
): AccountPurchaseItem[] {
  const map = new Map<string, AccountPurchaseItem>();
  for (const item of items) {
    const key = item.item_no || item.description;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        ...item,
        qty_months: [...item.qty_months],
        outlet_code: '*',
        outlet_name: '',
      });
      continue;
    }
    for (let i = 0; i < 12; i += 1) {
      existing.qty_months[i] = (existing.qty_months[i] ?? 0) + (item.qty_months[i] ?? 0);
    }
    existing.qty_ytd += item.qty_ytd;
    existing.qty_mtd += item.qty_mtd;
    existing.qty_prev_month += item.qty_prev_month;
    existing.qty_month_est += item.qty_month_est;
    if (!existing.base_uom && item.base_uom) existing.base_uom = item.base_uom;
    if (!existing.ipg && item.ipg) existing.ipg = item.ipg;
    if (!existing.packing && item.packing) existing.packing = item.packing;
    if (existing.inventory === 0 && item.inventory !== 0) existing.inventory = item.inventory;
    if (existing.fsm === 0 && item.fsm !== 0) existing.fsm = item.fsm;
    if (existing.ta == null && item.ta != null) existing.ta = item.ta;
    if (existing.unit_price == null) existing.unit_price = item.unit_price;
    else if (item.unit_price != null) {
      existing.unit_price = (existing.unit_price + item.unit_price) / 2;
    }
  }

  return [...map.values()].map((row) => recomputeTrendStatus(row, currentMonth));
}

export function isAlertItem(item: AccountPurchaseItem): boolean {
  return item.status === 'stopped' || item.status === 'declining' || item.month_trend === 'down';
}

/** Aggregate alert rows by item (for Alerts tab). */
export function aggregateAlertItems(
  items: AccountPurchaseItem[],
  currentMonth?: number,
): AccountPurchaseItem[] {
  return aggregatePurchaseItems(items.filter(isAlertItem), currentMonth);
}

function monthIndexFromSortKey(key: PurchaseSortKey): number | null {
  const idx = MONTH_SORT_KEYS.indexOf(key);
  return idx >= 0 ? idx : null;
}

export function sortPurchaseRows(
  rows: AccountPurchaseItem[],
  key: PurchaseSortKey,
  dir: 'asc' | 'desc',
): AccountPurchaseItem[] {
  const mul = dir === 'asc' ? 1 : -1;
  const statusRank: Record<PurchaseItemStatus, number> = {
    stopped: 0,
    declining: 1,
    ok: 2,
  };
  const trendRank: Record<PurchaseMonthTrend, number> = {
    down: 0,
    flat: 1,
    up: 2,
    new: 3,
  };
  const monthIdx = monthIndexFromSortKey(key);

  return [...rows].sort((a, b) => {
    let cmp = 0;
    if (monthIdx != null) {
      cmp = monthQty(a, monthIdx) - monthQty(b, monthIdx);
    } else {
      switch (key) {
        case 'item_no':
        case 'description':
        case 'base_uom':
        case 'outlet_code':
        case 'outlet_name':
        case 'ipg':
        case 'packing':
          cmp = (a[key] || '').localeCompare(b[key] || '', undefined, { sensitivity: 'base' });
          break;
        case 'unit_price':
          cmp = (a.unit_price ?? -Infinity) - (b.unit_price ?? -Infinity);
          break;
        case 'ta':
          cmp = (a.ta ?? -Infinity) - (b.ta ?? -Infinity);
          break;
        case 'inventory':
        case 'fsm':
        case 'qty_ytd':
        case 'qty_mtd':
        case 'qty_prev_month':
        case 'qty_month_est':
          cmp = a[key] - b[key];
          break;
        case 'month_trend':
          cmp = trendRank[a.month_trend] - trendRank[b.month_trend];
          break;
        case 'status':
          cmp = statusRank[a.status] - statusRank[b.status];
          break;
        default:
          cmp = 0;
      }
    }
    if (cmp !== 0) return cmp * mul;
    return a.item_no.localeCompare(b.item_no) || a.outlet_code.localeCompare(b.outlet_code);
  });
}

export async function fetchAccountPurchaseSummary(motherCode: string): Promise<AccountPurchaseSummary> {
  const code = motherCode.trim();
  if (!code) {
    throw new Error('Account code is required.');
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  try {
    const { data } = await getDbSupabase('crm').auth.getSession();
    const token = data.session?.access_token?.trim();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // proxy will reject unauthenticated
  }

  const response = await fetch(
    `/account-sales-api?mother_code=${encodeURIComponent(code)}`,
    { headers },
  );
  const json = (await response.json().catch(() => null)) as
    | (Record<string, unknown> & { error?: string; detail?: string; hint?: string })
    | null;

  if (!response.ok) {
    const detail =
      typeof json?.detail === 'string' && json.detail.trim() ? ` (${json.detail.trim()})` : '';
    const hint =
      typeof json?.hint === 'string' && json.hint.trim() ? ` ${json.hint.trim()}` : '';
    throw new Error(
      (json?.error || `Failed to load purchase history (${response.status}).`) + detail + hint,
    );
  }
  if (!json || typeof json !== 'object') {
    throw new Error('Invalid purchase history response.');
  }

  const outletsRaw = Array.isArray(json.outlets) ? json.outlets : [];
  const itemsRaw = Array.isArray(json.items) ? json.items : [];
  const metaRaw =
    json.meta && typeof json.meta === 'object' ? (json.meta as Record<string, unknown>) : {};

  return {
    outlets: outletsRaw
      .filter((o): o is Record<string, unknown> => !!o && typeof o === 'object')
      .map((o) => {
        const code = str(o.code) || str(o.no);
        const address = str(o.address) || [str(o.address_2), str(o.city)].filter(Boolean).join(', ');
        const postal =
          str(o.postal) ||
          str(o.post_code) ||
          str(o.postcode) ||
          extractSingaporePostal(address) ||
          '';
        return {
          code,
          name: str(o.name) || code,
          postal: postal || undefined,
          address: address || undefined,
        };
      })
      .filter((o) => o.code),
    items: itemsRaw
      .filter((i): i is Record<string, unknown> => !!i && typeof i === 'object')
      .map(normalizeItem)
      .filter((i) => i.item_no || i.description),
    meta: {
      mother_code: str(metaRaw.mother_code, code),
      today: str(metaRaw.today) || undefined,
      year: metaRaw.year == null ? undefined : num(metaRaw.year),
      year_start: str(metaRaw.year_start) || undefined,
      current_month: metaRaw.current_month == null ? undefined : num(metaRaw.current_month),
      day_of_month: metaRaw.day_of_month == null ? undefined : num(metaRaw.day_of_month),
      days_in_month: metaRaw.days_in_month == null ? undefined : num(metaRaw.days_in_month),
      month_start: str(metaRaw.month_start) || undefined,
      prev_month_start: str(metaRaw.prev_month_start) || undefined,
    },
  };
}
